import { useState, useMemo } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import Button from '../../components/ui/Button';
import { CATEGORIES_BY_USAGE } from '../../constants/categories';
import { IconX } from '@tabler/icons-react';

const normalise = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function AddMakeModal({ existingMakes, onClose }) {
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [description, setDescription] = useState('');
  const [supportedTypes, setSupportedTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Duplicate / fuzzy match detection
  const possibleMatches = useMemo(() => {
    if (!name || name.length < 2) return [];
    const normInput = normalise(name);
    return existingMakes.filter(m => {
      const normExisting = normalise(m.name);
      if (normExisting === normInput) return true;
      if (normExisting.includes(normInput) || normInput.includes(normExisting)) return true;
      return false;
    });
  }, [name, existingMakes]);

  const exactDuplicate = possibleMatches.some(m => normalise(m.name) === normalise(name));

  const toggleType = (code) => {
    setSupportedTypes(prev =>
      prev.includes(code) ? prev.filter(t => t !== code) : [...prev, code]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (exactDuplicate) {
      setError(`A make named "${name}" already exists.`);
      return;
    }
    if (supportedTypes.length === 0) {
      setError('Select at least one machine type this make produces.');
      return;
    }
    setError('');
    setSaving(true);

    try {
      await addDoc(collection(db, 'makes'), {
        name: name.trim(),
        normalisedName: normalise(name),
        country: country.trim() || null,
        description: description.trim() || null,
        supportedTypes,
        logoUrl: null,
        status: 'published',  // HQ-created records publish immediately
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null,
        createdByEmail: auth.currentUser?.email || null,
      });
      onClose();
    } catch (err) {
      console.error(err);
      setError('Failed to create make. ' + err.message);
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: 20,
    }}
    onClick={onClose}>
      <div style={{
        width: 480,
        maxHeight: '90vh',
        overflow: 'auto',
        background: 'var(--color-bg-primary)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-lg)',
      }}
      onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 24px',
          borderBottom: '0.5px solid var(--color-border-light)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>New make</div>
          <div onClick={onClose} style={{ cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>
            <IconX size={18} stroke={1.5} />
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          {/* Name */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>
              Make name *
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. John Deere"
              autoFocus
              required
            />
          </div>

          {/* Possible matches */}
          {possibleMatches.length > 0 && !exactDuplicate && (
            <div style={{
              padding: '10px 12px',
              background: 'var(--color-info-light)',
              border: '0.5px solid #B8D4EB',
              borderRadius: 'var(--radius-md)',
              marginBottom: 16,
              fontSize: 12,
            }}>
              <div style={{ color: 'var(--color-info)', fontWeight: 500, marginBottom: 4 }}>
                Possible matches found
              </div>
              <div style={{ color: 'var(--color-text-secondary)' }}>
                Did you mean one of these existing makes?
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {possibleMatches.map(m => (
                  <span key={m.id} style={{
                    padding: '3px 8px',
                    background: 'var(--color-bg-primary)',
                    borderRadius: 'var(--radius-full)',
                    fontSize: 11,
                    color: 'var(--color-text-primary)',
                  }}>
                    {m.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {exactDuplicate && (
            <div style={{
              padding: '10px 12px',
              background: 'var(--color-danger-light)',
              borderRadius: 'var(--radius-md)',
              marginBottom: 16,
              fontSize: 12,
              color: 'var(--color-danger)',
            }}>
              "{name}" already exists in the library.
            </div>
          )}

          {/* Country */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>
              Country of origin
            </label>
            <input
              type="text"
              value={country}
              onChange={e => setCountry(e.target.value)}
              placeholder="e.g. USA, Germany, Japan"
            />
          </div>

          {/* Supported types */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
              Machine types this make produces *
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CATEGORIES_BY_USAGE.map(cat => {
                const active = supportedTypes.includes(cat.code);
                return (
                  <div
                    key={cat.code}
                    onClick={() => toggleType(cat.code)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 'var(--radius-full)',
                      background: active ? 'var(--color-brand)' : 'var(--color-bg-primary)',
                      color: active ? '#fff' : 'var(--color-text-secondary)',
                      border: active ? 'none' : '0.5px solid var(--color-border-medium)',
                      fontSize: 12,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Brief description of the manufacturer..."
              rows={3}
              style={{ resize: 'vertical', minHeight: 60 }}
            />
          </div>

          {error && (
            <div style={{
              padding: '8px 12px',
              background: 'var(--color-danger-light)',
              color: 'var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 12,
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} type="button">Cancel</Button>
            <Button type="submit" loading={saving} disabled={exactDuplicate}>Create make</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
